// The Space a chat or a dashboard widget's tool call belongs to, as the server reads it.
//
// The Java harness picks the website, business data, brand and social profile a run acts on per Space
// (VMR `SpaceResources`). For a conversation it reads the Space from the conversation entity's
// `metadata.workspaceId`; for a widget's tool call, from a top-level `workspaceId` claim. Both only ever
// name one of the CALLER's own Spaces: the harness resolves them against the account's own Spaces, so an id
// from somewhere else matches nothing and the run simply has no Space.
//
// Same contract on iOS and Android (`ConversationSpace`): same key, same GUID rule, same entry points.
// A chat carries a Space only when it is opened FROM a Space — the Website tile, the Space Resources card's
// Reconnect, a "New goal" scoped to a Space, the Space's Agents-at-work tab, the Space's dashboard. Global
// entry points (home, the composer, notifications) carry none, and none is the right answer there.

/** The conversation-metadata key, and the tool-call body key, the harness reads the Space from. */
export const CONVERSATION_SPACE_KEY = 'workspaceId';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A Space id worth sending: a trimmed GUID. Anything else is no Space — never a half-formed one. */
export function conversationSpaceId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const id = value.trim();
  return GUID.test(id) ? id : undefined;
}

/**
 * The conversation-metadata key for the Wix site a chat was opened FOR, from outside Symphony: Aria in the Wix
 * dashboard links here with the site the owner was working on (`?new=1&site=<metasite id>`, seo-a11y design log
 * symphony-bm-landing/007). A scoping fact like the Space, not an instruction: the harness names it to the Maestro
 * only when it is one of the account's own Wix sites, so a foreign id scopes nothing. Web only: the link opens in a
 * browser.
 */
export const CONVERSATION_WIX_SITE_KEY = 'wixSiteId';

/** The metadata a NEW conversation is created with: `{ workspaceId }` for a Space, `{ wixSiteId }` for a site. */
export function conversationCreateMetadata(workspaceId: unknown, wixSiteId?: unknown): { workspaceId?: string; wixSiteId?: string } | undefined {
  const space = conversationSpaceId(workspaceId);
  const site = conversationSpaceId(wixSiteId);
  if (!space && !site) return undefined;
  return { ...(space ? { workspaceId: space } : {}), ...(site ? { [CONVERSATION_WIX_SITE_KEY]: site } : {}) };
}

/** The client's create body for `POST /api/conversations`; the Space and the site ride only when there is one. */
export function conversationCreateBody(workspaceId: unknown, wixSiteId?: unknown): { source: 'SCHEDULER'; workspaceId?: string; wixSiteId?: string } {
  return { source: 'SCHEDULER', ...conversationCreateMetadata(workspaceId, wixSiteId) };
}

/** The URL handoff's Space param (`?new=1&space=<id>`), stripped once the new thread exists. */
export const SPACE_HANDOFF_PARAM = 'space';

/** The URL handoff's site param (`?new=1&site=<metasite id>`), stripped once the new thread exists. */
export const SITE_HANDOFF_PARAM = 'site';

/**
 * The Space a chat handoff opens its NEW thread in. The imperative (store) handoff wins outright — when one is
 * pending, the URL is not consulted, so a stale `?space=` can never ride a handoff that named none. A URL
 * handoff counts only on a fresh thread (`?new=1`): a Space is stamped at creation, never on a continue.
 */
export function handoffSpaceId(
  pending: { workspaceId?: string } | null | undefined,
  query: Record<string, string | string[] | undefined>,
): string | null {
  if (pending) return conversationSpaceId(pending.workspaceId) ?? null;
  if (query.new !== '1') return null;
  return conversationSpaceId(query[SPACE_HANDOFF_PARAM]) ?? null;
}

/**
 * The Wix site a NEW thread is opened for (see {@link CONVERSATION_WIX_SITE_KEY}). URL only, and only on a fresh thread
 * (`?new=1`), like the Space: an imperative handoff names none, so a stale `?site=` never rides one.
 */
export function handoffSiteId(
  pending: unknown,
  query: Record<string, string | string[] | undefined>,
): string | null {
  if (pending) return null;
  if (query.new !== '1') return null;
  return conversationSpaceId(query[SITE_HANDOFF_PARAM]) ?? null;
}

/**
 * The Space a dashboard belongs to, from the slug its host renders. Only a slug that IS one of the account's
 * Spaces names one: the home dashboard and a global drill-in page pass no slug, and a slug that matches no Space
 * (a pack's dashboard, a list still loading) is no Space rather than a guess.
 */
export function spaceIdForSlug(
  slug: string | undefined,
  spaces: ReadonlyArray<{ id: string; slug?: string | null }>,
): string | undefined {
  if (!slug) return undefined;
  return conversationSpaceId(spaces.find((space) => space.slug === slug)?.id);
}

/** A widget tool call's body: the Space rides top-level, beside the tool, never inside its params. */
export function widgetToolCallBody(tool: string, params: unknown, workspaceId?: unknown, workspaceSlug?: string): {
  tool: string;
  params: unknown;
  workspaceId?: string;
  workspaceSlug?: string;
} {
  const id = conversationSpaceId(workspaceId);
  if (id) return { tool, params: params ?? {}, workspaceId: id };
  // No id (the Spaces list failed or is not loaded): the dashboard's slug, which /api/tool-call resolves against
  // the caller's own Spaces, so the read still knows its business instead of going out with none.
  const slug = typeof workspaceSlug === 'string' ? workspaceSlug.trim() : '';
  return { tool, params: params ?? {}, ...(slug ? { workspaceSlug: slug } : {}) };
}
